import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Calendar, ArrowLeft, Plus, Edit, Trash2, Users, X, Video, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RoomsSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    capacity: '',
    equipment: '',
    idle_video: ''
  });

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      toast.error('Erro ao carregar salas');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato inválido. Use MP4, WebM, OGG ou MOV');
      return;
    }

    // Validar tamanho (100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('O vídeo deve ter no máximo 100MB');
      return;
    }

    setUploadingVideo(true);
    try {
      const videoFormData = new FormData();
      videoFormData.append('file', file);

      const response = await axios.post(`${API}/upload-video`, videoFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData({ ...formData, idle_video: response.data.url });
      toast.success('Vídeo enviado com sucesso!');
    } catch (error) {
      toast.error('Erro ao enviar vídeo');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const equipmentArray = formData.equipment.split(',').map(e => e.trim()).filter(e => e);
      const data = {
        name: formData.name,
        capacity: parseInt(formData.capacity),
        equipment: equipmentArray,
        idle_video: formData.idle_video || null
      };
      // If editing and video was explicitly removed, send null to clear it
      if (editingRoom && editingRoom.idle_video && !formData.idle_video) {
        data.idle_video = null;
      }

      if (editingRoom) {
        await axios.put(`${API}/rooms/${editingRoom.id}`, data);
        toast.success('Sala atualizada com sucesso!');
      } else {
        await axios.post(`${API}/rooms`, data);
        toast.success('Sala criada com sucesso!');
      }

      setShowForm(false);
      setEditingRoom(null);
      setFormData({ name: '', capacity: '', equipment: '', idle_video: '' });
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar sala');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setFormData({
      name: room.name,
      capacity: room.capacity.toString(),
      equipment: room.equipment.join(', '),
      idle_video: room.idle_video || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta sala?')) {
      return;
    }

    try {
      await axios.delete(`${API}/rooms/${roomId}`);
      toast.success('Sala excluída com sucesso!');
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir sala');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRoom(null);
    setFormData({ name: '', capacity: '', equipment: '', idle_video: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-manrope font-bold tracking-tight">Configuração de Salas</h1>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium font-inter">{user?.name}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-manrope font-bold tracking-tight mb-2">
              Gerenciar Salas de Reunião
            </h2>
            <p className="text-muted-foreground font-inter">
              {rooms.length} {rooms.length === 1 ? 'sala cadastrada' : 'salas cadastradas'}
            </p>
          </div>
          {!showForm && (
            <Button
              data-testid="add-room-button"
              onClick={() => setShowForm(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 px-6 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nova Sala
            </Button>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-2xl border-2 border-border/50 p-8 mb-8 shadow-lg">
            <h3 className="text-2xl font-manrope font-bold tracking-tight mb-6">
              {editingRoom ? 'Editar Sala' : 'Nova Sala'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Nome da Sala *
                </Label>
                <Input
                  id="name"
                  data-testid="room-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Sala de Reunião 3"
                  required
                  className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="capacity" className="text-sm font-medium">
                  Capacidade (pessoas) *
                </Label>
                <Input
                  id="capacity"
                  data-testid="room-capacity-input"
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  placeholder="Ex: 10"
                  required
                  className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="equipment" className="text-sm font-medium">
                  Equipamentos (separados por vírgula) *
                </Label>
                <Input
                  id="equipment"
                  data-testid="room-equipment-input"
                  type="text"
                  value={formData.equipment}
                  onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                  placeholder="Ex: Projetor, TV 55 polegadas, Quadro Branco"
                  required
                  className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
                />
              </div>

              {/* Upload de Vídeo */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Vídeo para Sala Ociosa (opcional)
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Este vídeo será exibido no monitor quando não houver reuniões no dia.
                  <br />
                  <strong>Dimensão recomendada:</strong> 1920×1080 (Full HD) ou 1080×1920 (Vertical)
                  <br />
                  <strong>Formato:</strong> MP4, WebM ou MOV | <strong>Máximo:</strong> 100MB
                </p>
                
                {formData.idle_video ? (
                  <div className="relative bg-black rounded-lg overflow-hidden">
                    <video 
                      src={formData.idle_video.startsWith('http') ? formData.idle_video : `${BACKEND_URL}${formData.idle_video}`}
                      className="w-full h-48 object-contain"
                      controls
                      muted
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, idle_video: '' })}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border/50 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploadingVideo ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Clique para enviar vídeo
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            MP4, WebM ou MOV (máx. 100MB)
                          </p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/ogg,video/quicktime"
                      onChange={handleVideoUpload}
                      className="hidden"
                      disabled={uploadingVideo}
                    />
                  </label>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={handleCancel}
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="save-room-button"
                  disabled={loading}
                  className="flex-1 h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  {loading ? 'Salvando...' : editingRoom ? 'Atualizar Sala' : 'Criar Sala'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Rooms List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {rooms.map((room) => (
            <div
              key={room.id}
              data-testid={`room-card-${room.id}`}
              className="bg-white rounded-2xl border-2 border-border/50 p-6 shadow-lg hover:shadow-xl transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-2xl font-manrope font-bold tracking-tight mb-2">
                    {room.name}
                  </h3>
                  <div className="flex items-center gap-2 text-muted-foreground mb-4">
                    <Users className="w-4 h-4" />
                    <span className="font-mono font-medium">Capacidade: {room.capacity} pessoas</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-room-button-${room.id}`}
                    onClick={() => handleEdit(room)}
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-primary hover:bg-primary/10"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-room-button-${room.id}`}
                    onClick={() => handleDelete(room.id)}
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Equipamentos:</p>
                <div className="flex flex-wrap gap-2">
                  {room.equipment.map((item, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-3 py-1.5 rounded-full bg-secondary/20 text-foreground font-inter border border-secondary/30"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {rooms.length === 0 && !showForm && (
          <div className="text-center py-16 px-4">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-manrope font-semibold mb-2">Nenhuma sala cadastrada</h3>
            <p className="text-muted-foreground font-inter mb-6">Crie sua primeira sala de reunião</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 px-6"
            >
              <Plus className="w-5 h-5 mr-2" />
              Criar Primeira Sala
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}